import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TablePagination,
  useTheme,
  useMediaQuery,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Images } from "../../utils/Images";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import DownloadIcon from "@mui/icons-material/Download";
import { fetchUserTenBEs } from "../../store/slices/userTenbeSlice";
import { downloadTenbeCertificate } from "../../store/slices/transactionSlice";

const Form10B = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(
    "(min-width:600px) and (max-width:900px)"
  );
  const isSmallMediumScreen = useMediaQuery(
    "(min-width:600px) and (max-width:800px)"
  );
  const { user } = useSelector((state) => state.auth);
  const { data: tenbeData, loading, error } = useSelector((state) => state.userTenbe);
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);

  // iOS Safari does not show placeholder for input[type="date"], and can behave oddly unless toggled from text->date on focus
  const isIOS = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }, []);

  const getDateFieldType = (value) => (isIOS && !value ? "text" : "date");
  const handleDateFocus = (e) => {
    if (isIOS) e.target.type = "date";
  };
  const handleDateBlur = (e) => {
    if (isIOS && !e.target.value) e.target.type = "text";
  };

  // Fetch 10B list on component mount
  useEffect(() => {
    if (user?.id) {
      dispatch(fetchUserTenBEs(user.id));
    }
  }, [dispatch, user?.id]);

  // Transform API data to match component structure
  // tenbeData should already be the ten_bes array from Redux slice
  const rawForms = Array.isArray(tenbeData) ? tenbeData : [];
  
  // Transform API response to match component's expected format
  const forms = rawForms.map((item) => ({
    id: item.id || item.transactionId,
    date: item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    }) : item.financial_year || "",
    transactionId: item.transactionId || `#${item.id}` || "",
    amount: item.amount ? `₹${Number(item.amount).toLocaleString('en-IN')}` : "₹0",
    status: item.downloadTenbe ? "Downloaded" : "Available",
    isDownloaded: item.downloadTenbe === true, // Track if already downloaded
    pan: item.pan || item.panNumber,
    financial_year: item.financial_year || item.financialYear,
    pdf_url: item.pdf_url || item.pdfUrl,
    file_number: item.file_number || item.fileNumber,
    ...item
  }));

  // Filter forms based on search, dates, and status
  const filteredForms = useMemo(() => {
    return forms.filter((form) => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const transactionIdMatch = form.transactionId?.toLowerCase().includes(searchLower);
        if (!transactionIdMatch) return false;
      }

      // Date filters - use createdAt from original data if available
      if (fromDate || toDate) {
        // Try to get the original date from the item
        const formDateStr = form.createdAt || form.date;
        if (!formDateStr) return false;
        
        const formDate = new Date(formDateStr);
        if (isNaN(formDate.getTime())) return false;

        if (fromDate) {
          const from = new Date(fromDate);
          from.setHours(0, 0, 0, 0);
          if (formDate < from) return false;
        }

        if (toDate) {
          const to = new Date(toDate);
          to.setHours(23, 59, 59, 999);
          if (formDate > to) return false;
        }
      }

      // Status filter
      if (status) {
        const formStatus = form.status?.toLowerCase();
        if (status === "available" && formStatus !== "available") return false;
        if (status === "pending" && formStatus !== "pending") return false;
        if (status === "downloaded" && formStatus !== "downloaded") return false;
      }

      return true;
    });
  }, [forms, searchTerm, fromDate, toDate, status]);

  // Calculate pagination
  const totalPages = Math.max(1, Math.ceil(filteredForms.length / rowsPerPage));
  const paginatedForms = filteredForms.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, fromDate, toDate, status, rowsPerPage]);

  // Generate page numbers to display
  const getPageNumbers = () => {
    if (totalPages === 0) return [1];
    if (totalPages === 1) return [1];
    
    const pages = [];
    if (page === 0) {
      // On first page, show up to 3 pages
      for (let i = 1; i <= Math.min(3, totalPages); i++) {
        pages.push(i);
      }
    } else if (page === totalPages - 1) {
      // On last page, show previous pages
      const start = Math.max(1, totalPages - 2);
      for (let i = start; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // On middle pages, show current and previous pages
      const start = Math.max(1, page);
      for (let i = start; i <= Math.min(start + 2, totalPages); i++) {
        pages.push(i);
      }
    }
    return pages;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleBackToMenu = () => {
    navigate("/profile");
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownload = async (row, data) => {
    try {
      // Update download status (this will update the backend)
      await dispatch(downloadTenbeCertificate({
        pan: data?.pan || data?.panNumber || row.pan,
        financialYear: data?.financial_year || data?.financialYear || row.financial_year
      })).unwrap();

      // Open PDF URL from API response (pdf_url field)
      const pdfUrl = row.pdf_url || row.pdfUrl || data?.pdf_url || data?.pdfUrl;
      if (pdfUrl) {
        window.open(pdfUrl, "_blank");
      } else {
        // Fallback to constructing URL from file_number
        const baseURL = import.meta.env.VITE_API_BASE_URL;
        const fileNumber = row.file_number || row.fileNumber || data?.file_number;
        if (fileNumber) {
          const fileUrl = `${baseURL}/${fileNumber}`;
          window.open(fileUrl, "_blank");
        } else {
          alert("PDF URL not available. Please contact support.");
        }
      }
      
      // Refresh the 10B list to update the downloadTenbe status
      if (user?.id) {
        dispatch(fetchUserTenBEs(user.id));
      }
    } catch (error) {
      console.error("Failed to download 10B certificate:", error);
      alert("Failed to download 10B certificate. Please try again.");
    }
  };

  return (
    <Box>
      {/* Mobile header with back button */}
      {isMobile && (
        <Box
          onClick={handleBackToMenu}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            marginBottom: "20px",
            width: "100%",
          }}
        >
          <Box
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="#1D1D1B"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "14px",
              lineHeight: "100%",
              letterSpacing: "0%",
              color: "#1D1D1B",
            }}
          >
            10B Form
          </Typography>
        </Box>
      )}
      {!isMobile && (
        <Paper
          elevation={2}
          sx={{
            overflow: isMediumScreen ? "auto" : "visible",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1) !important",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#FFFFFF",
              borderRadius: "12px",
              p: 3,
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1) !important", // Force shadow to always appear
            }}
          >
            {/* Header and Search Section */}
            <Box
              sx={{
                display: "flex",
                flexDirection: isSmallMediumScreen ? "column" : "row",
                justifyContent: isSmallMediumScreen ? "flex-start" : "end",
                alignItems: isSmallMediumScreen ? "flex-start" : "center",
                mb: "20px",
                gap: isSmallMediumScreen ? "16px" : "16px",
                minWidth: 0, // Allow shrinking
                overflow: "hidden", // Prevent overflow
                flexWrap: "wrap", // Allow wrapping
              }}
            >
              {/* First row - Header and Search (only on smaller medium screens) - HIDDEN */}
              {false && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    gap: "16px",
                  }}
                >
                  <Typography
                    component="h1"
                    sx={{
                      fontWeight: 600,
                      fontStyle: "SemiBold",
                      fontSize: "22px",
                      lineHeight: "22.34px",
                      letterSpacing: "0%",
                      color: "#1D1D1B",
                      flexShrink: 0,
                      margin: 0,
                      padding: 0,
                    }}
                  >
                    Download 10B Form
                  </Typography>

                  {/* Search field for medium screens */}
                  <TextField
                    placeholder="Search by Transaction Id"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    sx={{
                      minWidth: "200px",
                      maxWidth: "250px",
                      flexGrow: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        paddingTop: "10px",
                        paddingRight: "20px",
                        paddingBottom: "10px",
                        paddingLeft: "20px",
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #BEBFD0",
                        "& fieldset": {
                          border: "none",
                        },
                        "&:hover fieldset": {
                          border: "none",
                        },
                        "&.Mui-focused fieldset": {
                          border: "none",
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: 0,
                        margin: 0,
                        fontWeight: 500,
                        fontStyle: "Medium",
                        fontSize: "13px",
                        lineHeight: "100%",
                        letterSpacing: "0%",
                        verticalAlign: "middle",
                        color: "#686866",
                        "&::placeholder": {
                          fontWeight: 500,
                          fontStyle: "Medium",
                          fontSize: "13px",
                          lineHeight: "100%",
                          letterSpacing: "0%",
                          verticalAlign: "middle",
                          color: "#686866",
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>
              )}

              {/* Header for desktop and larger medium screens */}
              {!isSmallMediumScreen && (
                <Typography
                  component="h1"
                  sx={{
                    fontWeight: 600,
                    fontStyle: "SemiBold",
                    fontSize: "22px",
                    lineHeight: "22.34px",
                    letterSpacing: "0%",
                    color: "#1D1D1B",
                    flexShrink: 0,
                    margin: 0,
                    padding: 0,
                    order: 1, // Always stay on the left
                  }}
                >
                  Download 10B Form
                </Typography>
              )}

              {/* Header for 800px to 600px screens with filter button */}
              {isSmallMediumScreen && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    order: 1,
                  }}
                >
                  <Typography
                    component="h1"
                    sx={{
                      fontWeight: 600,
                      fontStyle: "SemiBold",
                      fontSize: "22px",
                      lineHeight: "22.34px",
                      letterSpacing: "0%",
                      color: "#1D1D1B",
                      flexShrink: 0,
                      margin: 0,
                      padding: 0,
                    }}
                  >
                    Download 10B Form
                  </Typography>
                  <IconButton
                    onClick={() => setShowFilters(!showFilters)}
                    sx={{
                      backgroundColor: "#1D1D1B  !important",
                      borderRadius: "8px",
                      width: "40px",
                      height: "40px",
                    }}
                  >
                    <Box
                      component="img"
                      src={showFilters ? Images.whiteClose : Images.filter}
                      alt={showFilters ? "Close" : "Filter"}
                      sx={{ width: "20px", height: "20px" }}
                    />
                  </IconButton>
                </Box>
              )}

              {/* Search Field - Top Right */}
              {!isSmallMediumScreen && (
                <Box
                  sx={{
                    flexShrink: 0,
                    order: 2, // Always stay on the right
                    marginLeft: "auto",
                    flex: "0 1 auto", // Don't grow by default
                    maxWidth: "300px", // Limit default width
                    "@media (max-width: 1250px)": {
                      flex: "1 1 auto", // Fill space when filters come down
                      maxWidth: "100%",
                    },
                  }}
                >
                  <TextField
                    placeholder="Search by Transaction Id"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    sx={{
                      width: "100%", // Fill the container
                      minWidth: "220px",
                      "@media (min-width: 1250px)": {
                        maxWidth: "300px", // Limit width on large screens
                      },
                      "@media (max-width: 1250px)": {
                        maxWidth: "none", // No limit when filters come down
                      },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        paddingTop: "14px",
                        paddingRight: "24px",
                        paddingBottom: "14px",
                        paddingLeft: "24px",
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #BEBFD0",
                        "& fieldset": {
                          border: "none",
                        },
                        "&:hover fieldset": {
                          border: "none",
                        },
                        "&.Mui-focused fieldset": {
                          border: "none",
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: 0,
                        margin: 0,
                        fontWeight: 500,
                        fontStyle: "Medium",
                        fontSize: "14px",
                        lineHeight: "100%",
                        letterSpacing: "0%",
                        verticalAlign: "middle",
                        color: "#686866",
                        "&::placeholder": {
                          fontWeight: 500,
                          fontStyle: "Medium",
                          fontSize: "14px",
                          lineHeight: "100%",
                          letterSpacing: "0%",
                          verticalAlign: "middle",
                          color: "#686866",
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>
              )}

              {/* Date Fields and Status Filter - Separate Box */}
              {!isSmallMediumScreen && (
                <Box
                  sx={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    flexWrap: "nowrap",
                    flexShrink: 0,
                    order: 3,
                  }}
                >
                  {/* From Date */}
                  <TextField
                    type={getDateFieldType(fromDate)}
                    placeholder="From date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    onFocus={handleDateFocus}
                    onBlur={handleDateBlur}
                    variant="outlined"
                    sx={{
                      minWidth: isMediumScreen ? "120px" : "140px",
                      maxWidth: isMediumScreen ? "140px" : "160px",
                      flex: "0 1 auto",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        paddingTop: isMediumScreen ? "10px" : "13px",
                        paddingRight: isMediumScreen ? "16px" : "24px",
                        paddingBottom: isMediumScreen ? "10px" : "13px",
                        paddingLeft: isMediumScreen ? "16px" : "24px",
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #BEBFD0",
                        "& fieldset": {
                          border: "none",
                        },
                        "&:hover fieldset": {
                          border: "none",
                        },
                        "&.Mui-focused fieldset": {
                          border: "none",
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: 0,
                        margin: 0,
                        fontWeight: 500,
                        fontStyle: "Medium",
                        fontSize: "14px",
                        lineHeight: "100%",
                        letterSpacing: "0%",
                        verticalAlign: "middle",
                        color: "#686866",
                        "&::placeholder": {
                          fontWeight: 500,
                          fontStyle: "Medium",
                          fontSize: "14px",
                          lineHeight: "100%",
                          letterSpacing: "0%",
                          verticalAlign: "middle",
                          color: "#686866",
                          opacity: 1,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        display: "none",
                      },
                    }}
                  />

                  {/* To Date */}
                  <TextField
                    type={getDateFieldType(toDate)}
                    placeholder="To date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    onFocus={handleDateFocus}
                    onBlur={handleDateBlur}
                    variant="outlined"
                    sx={{
                      minWidth: isMediumScreen ? "120px" : "140px",
                      maxWidth: isMediumScreen ? "140px" : "160px",
                      flex: "0 1 auto",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        paddingTop: isMediumScreen ? "10px" : "13px",
                        paddingRight: isMediumScreen ? "16px" : "24px",
                        paddingBottom: isMediumScreen ? "10px" : "13px",
                        paddingLeft: isMediumScreen ? "16px" : "24px",
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #BEBFD0",
                        "& fieldset": {
                          border: "none",
                        },
                        "&:hover fieldset": {
                          border: "none",
                        },
                        "&.Mui-focused fieldset": {
                          border: "none",
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: 0,
                        margin: 0,
                        fontWeight: 500,
                        fontStyle: "Medium",
                        fontSize: "14px",
                        lineHeight: "100%",
                        letterSpacing: "0%",
                        verticalAlign: "middle",
                        color: "#686866",
                        "&::placeholder": {
                          fontWeight: 500,
                          fontStyle: "Medium",
                          fontSize: "14px",
                          lineHeight: "100%",
                          letterSpacing: "0%",
                          verticalAlign: "middle",
                          color: "#686866",
                          opacity: 1,
                        },
                      },
                      "& .MuiInputLabel-root": {
                        display: "none",
                      },
                    }}
                  />

                  {/* Status Filter */}
                  <FormControl
                    sx={{
                      minWidth: isMediumScreen ? "120px" : "140px",
                      maxWidth: isMediumScreen ? "140px" : "160px",
                      flex: "0 1 auto",
                    }}
                  >
                    <Select
                      value={status}
                      displayEmpty
                      onChange={(e) => setStatus(e.target.value)}
                      sx={{
                        borderRadius: "8px",
                        paddingTop: isMediumScreen ? "10px" : "15px",
                        paddingRight: isMediumScreen ? "16px" : "24px",
                        paddingBottom: isMediumScreen ? "10px" : "9px",
                        paddingLeft: isMediumScreen ? "16px" : "24px",
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #BEBFD0",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "& .MuiSelect-select": {
                          padding: 0,
                          margin: 0,
                          fontWeight: 500,
                          fontStyle: "Medium",
                          fontSize: "14px",
                          lineHeight: "100%",
                          letterSpacing: "0%",
                          verticalAlign: "middle",
                          color: status ? "#1D1D1B" : "#686866",
                        },
                        "& .MuiSelect-icon": {
                          color: "#686866",
                        },
                      }}
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <span style={{ color: "#686866" }}>Status</span>
                          );
                        }
                        return selected;
                      }}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="available">Available</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}

              {/* Filter Section for 800px to 600px screens */}
              {isSmallMediumScreen && showFilters && (
                <Box
                  sx={{
                    order: 2,
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <TextField
                    placeholder="Search By Transaction ID"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="outlined"
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        border: "1px solid #DDD",
                        padding: "12px 20px 12px 16px",
                        paddingRight: "0px",
                        height: "40px",
                        "& fieldset": { border: "none" },
                      },
                      "& .MuiInputBase-input": {
                        fontSize: "14px",
                        color: "#686866",
                        padding: 0,
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            component="img"
                            src={Images.search}
                            alt="Search"
                            sx={{ width: "18px", height: "18px" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <TextField
                      type={getDateFieldType(fromDate)}
                      placeholder="From date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      onFocus={handleDateFocus}
                      onBlur={handleDateBlur}
                      variant="outlined"
                      sx={{
                        flex: "1",
                        minWidth: "140px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: "#fff",
                          border: "1px solid #DDD",
                          padding: "12px 16px",
                          height: "40px",
                          "& fieldset": { border: "none" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "14px",
                          color: "#686866",
                          padding: 0,
                        },
                        "& .MuiInputLabel-root": {
                          display: "none",
                        },
                      }}
                    />
                    <TextField
                      type={getDateFieldType(toDate)}
                      placeholder="To date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      onFocus={handleDateFocus}
                      onBlur={handleDateBlur}
                      variant="outlined"
                      sx={{
                        flex: "1",
                        minWidth: "140px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: "#fff",
                          border: "1px solid #DDD",
                          padding: "12px 16px",
                          height: "40px",
                          "& fieldset": { border: "none" },
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "14px",
                          color: "#686866",
                          padding: 0,
                        },
                        "& .MuiInputLabel-root": {
                          display: "none",
                        },
                      }}
                    />
                    <FormControl
                      sx={{
                        flex: "1",
                        minWidth: "140px",
                      }}
                    >
                      <Select
                        value={status}
                        displayEmpty
                        onChange={(e) => setStatus(e.target.value)}
                        sx={{
                          height: "40px",
                          backgroundColor: "#fff",
                          border: "1px solid #DDD",
                          borderRadius: "8px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "& .MuiSelect-select": {
                            fontSize: "14px",
                            color: status ? "#1D1D1B" : "#686866",
                            padding: "12px 16px",
                          },
                        }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return (
                              <span style={{ color: "#686866" }}>Status</span>
                            );
                          }
                          return selected;
                        }}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="available">Available</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Table */}
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            )}
            {error && (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
                <Typography sx={{ color: "#F04951" }}>
                  {error || "Failed to load 10B forms. Please try again."}
                </Typography>
              </Box>
            )}
            {!loading && !error && filteredForms.length === 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
                <Typography sx={{ color: "#5E5E5E" }}>
                  No 10B forms available.
                </Typography>
              </Box>
            )}
            {!loading && !error && filteredForms.length > 0 && (
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: "4px",
                boxShadow: "none",
                border: "none",
                overflowX: isMediumScreen ? "auto" : "hidden",
              }}
            >
              <Table
                sx={{
                  minWidth: isMediumScreen ? 600 : "auto",
                  tableLayout: "fixed",
                  width: "100%",
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: "#F5F8ED",
                      padding: "30px",
                      "& .MuiTableCell-head": {
                        fontWeight: 600,
                        fontStyle: "SemiBold",
                        fontSize: isMediumScreen ? "14px" : "16px",
                        lineHeight: "16px",
                        letterSpacing: "0%",
                        color: "#1D1D1B",
                        borderBottom: "1px solid #E0E0E0",
                        whiteSpace: "nowrap",
                        padding: isMediumScreen ? "10px 12px" : "16px",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontStyle: "SemiBold",
                        fontSize: "16px",
                        lineHeight: "16px",
                        letterSpacing: "0%",
                        color: "#1D1D1B",
                        borderBottom: "1px solid #E0E0E0",
                        whiteSpace: "nowrap",
                        width: "25%",
                      }}
                    >
                      Donation Date
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontStyle: "SemiBold",
                        fontSize: "16px",
                        lineHeight: "16px",
                        letterSpacing: "0%",
                        color: "#1D1D1B",
                        borderBottom: "1px solid #E0E0E0",
                        whiteSpace: "nowrap",
                        width: "30%",
                      }}
                    >
                      Transaction ID
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontStyle: "SemiBold",
                        fontSize: "16px",
                        lineHeight: "16px",
                        letterSpacing: "0%",
                        color: "#1D1D1B",
                        borderBottom: "1px solid #E0E0E0",
                        width: "20%",
                      }}
                    >
                      Amount
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontStyle: "SemiBold",
                        fontSize: "16px",
                        lineHeight: "16px",
                        letterSpacing: "0%",
                        color: "#1D1D1B",
                        borderBottom: "1px solid #E0E0E0",
                        width: "25%",
                        textAlign: "center",
                      }}
                    >
                      Download
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedForms.map((form) => (
                    <TableRow
                      key={form.id}
                      sx={{
                        "&:hover": { backgroundColor: "#F8F9FA" },
                        "&:last-child td": { borderBottom: 0 },
                        height: "60px",
                        "& .MuiTableCell-body": {
                          padding: isMediumScreen ? "8px 12px" : "16px",
                          fontSize: isMediumScreen ? "14px" : "16px",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontFamily: "Red Hat Display",
                          fontWeight: 500,
                          fontStyle: "Medium",
                          fontSize: "16px",
                          lineHeight: "16px",
                          letterSpacing: "0%",
                          color: "#1D1D1B",
                          whiteSpace: "nowrap",
                          width: "25%",
                        }}
                      >
                        {form.date}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Red Hat Display",
                          fontWeight: 500,
                          fontStyle: "Medium",
                          fontSize: "16px",
                          lineHeight: "16px",
                          letterSpacing: "0%",
                          color: "#1D1D1B",
                          whiteSpace: "nowrap",
                          width: "30%",
                        }}
                      >
                        {form.transactionId}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Red Hat Display",
                          fontWeight: 500,
                          fontStyle: "Medium",
                          fontSize: "16px",
                          lineHeight: "16px",
                          letterSpacing: "0%",
                          color: "#1D1D1B",
                          width: "20%",
                        }}
                      >
                        {form.amount}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "25%",
                          textAlign: "center",
                        }}
                      >
                        <Button
                          variant="contained"
                          startIcon={<DownloadIcon sx={{ fontSize: "18px" }} />}
                          onClick={() => handleDownload(form.id || form.transactionId, form)}
                          sx={{
                            backgroundColor: form.isDownloaded ? "#3BC37F" : "#6A994E",
                            borderRadius: "30px",
                            boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.15)",
                            textTransform: "none",
                            fontSize: "14px",
                            fontWeight: 600,
                            padding: "6px 20px",
                            minWidth: "140px",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              backgroundColor: form.isDownloaded ? "#2FA86F" : "#5A8B41",
                              boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          {form.isDownloaded ? "Downloaded" : "Download 10B Form"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            )}

            {/* Pagination */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
                flexWrap: "nowrap",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                  minWidth: 0,
                  mr: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#5E5E5E", whiteSpace: "nowrap" }}
                  >
                    Rows per page:
                  </Typography>
                  <Select
                    value={rowsPerPage}
                    onChange={handleChangeRowsPerPage}
                    size="small"
                    sx={{
                      minWidth: "60px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#E0E0E0",
                      },
                    }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </Select>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#5E5E5E",
                    whiteSpace: "nowrap",
                    flexShrink: 1,
                  }}
                >
                  Row {filteredForms.length === 0 ? 0 : page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredForms.length)} of {filteredForms.length}{" "}
                  rows
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "nowrap",
                  flexShrink: 0,
                }}
              >
                {/* Back Button */}
                <Box
                  onClick={() => setPage(page - 1)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #E0E0E0",
                    backgroundColor: "#FFFFFF",
                    cursor: page === 0 ? "not-allowed" : "pointer",
                    opacity: page === 0 ? 0.5 : 1,
                    "&:hover": {
                      backgroundColor: page === 0 ? "#FFFFFF" : "#F5F5F5",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: "16px",
                      height: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 12L6 8L10 4"
                        stroke="#5E5E5E"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#5E5E5E",
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  >
                    Back
                  </Typography>
                </Box>

                {/* Page Numbers */}
                {getPageNumbers().map((pageNum) => (
                  <Box
                    key={pageNum}
                    onClick={() => setPage(pageNum - 1)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "32px",
                      height: "32px",
                      borderRadius: "6px",
                      backgroundColor:
                        page === pageNum - 1 ? "#1D1D1B" : "#FFFFFF",
                      border:
                        page === pageNum - 1 ? "none" : "1px solid #E0E0E0",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor:
                          page === pageNum - 1 ? "#2A2A2A" : "#F5F5F5",
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        color: page === pageNum - 1 ? "#FFFFFF" : "#5E5E5E",
                      }}
                    >
                      {pageNum}
                    </Typography>
                  </Box>
                ))}

                {/* Next Button */}
                <Box
                  onClick={() => setPage(page + 1)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #E0E0E0",
                    backgroundColor: "#FFFFFF",
                    cursor: page >= totalPages - 1 ? "not-allowed" : "pointer",
                    opacity: page >= totalPages - 1 ? 0.5 : 1,
                    "&:hover": {
                      backgroundColor: page >= totalPages - 1 ? "#FFFFFF" : "#F5F5F5",
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#5E5E5E",
                      fontWeight: 500,
                      fontSize: "14px",
                    }}
                  >
                    Next
                  </Typography>
                  <Box
                    sx={{
                      width: "16px",
                      height: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 4L10 8L6 12"
                        stroke="#5E5E5E"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}
      {isMobile && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              mb: "20px",
            }}
          >
            <TextField
              placeholder="Search By Transaction ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  border: "1px solid #DDD",
                  padding: "12px 20px 12px 16px",
                  paddingRight: "0px",
                  height: "40px",
                  "& fieldset": { border: "none" },
                },
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  color: "#686866",
                  padding: 0,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="img"
                      src={Images.search}
                      alt="Search"
                      sx={{ width: "18px", height: "18px" }}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <IconButton
              onClick={() => setShowFilters(!showFilters)}
              sx={{
                backgroundColor: "#1D1D1B !important",
                borderRadius: "8px",
                width: "40px",
                height: "40px",
              }}
            >
              <Box
                component="img"
                src={showFilters ? Images.whiteClose : Images.filter}
                alt={showFilters ? "Close" : "Filter"}
                sx={{ width: "20px", height: "20px" }}
              />
            </IconButton>
          </Box>

          {/* Filter Section for Mobile */}
          {showFilters && (
            <Box
              sx={{
                mb: "20px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  type={getDateFieldType(fromDate)}
                  placeholder="From date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  onFocus={handleDateFocus}
                  onBlur={handleDateBlur}
                  variant="outlined"
                  sx={{
                    flex: "1",
                    minWidth: "140px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      border: "1px solid #DDD",
                      padding: "12px 16px",
                      height: "40px",
                      "& fieldset": { border: "none" },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "14px",
                      color: "#686866",
                      padding: 0,
                    },
                    "& .MuiInputLabel-root": {
                      display: "none",
                    },
                  }}
                />
                <TextField
                  type={getDateFieldType(toDate)}
                  placeholder="To date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  onFocus={handleDateFocus}
                  onBlur={handleDateBlur}
                  variant="outlined"
                  sx={{
                    flex: "1",
                    minWidth: "140px",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      border: "1px solid #DDD",
                      padding: "12px 16px",
                      height: "40px",
                      "& fieldset": { border: "none" },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "14px",
                      color: "#686866",
                      padding: 0,
                    },
                    "& .MuiInputLabel-root": {
                      display: "none",
                    },
                  }}
                />
                <FormControl
                  sx={{
                    flex: "1",
                    minWidth: "140px",
                  }}
                >
                  <Select
                    value={status}
                    displayEmpty
                    onChange={(e) => setStatus(e.target.value)}
                    sx={{
                      height: "40px",
                      backgroundColor: "#fff",
                      border: "1px solid #DDD",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "& .MuiSelect-select": {
                        fontSize: "14px",
                        color: status ? "#1D1D1B" : "#686866",
                        padding: "12px 16px",
                      },
                    }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span style={{ color: "#686866" }}>Status</span>;
                      }
                      return selected;
                    }}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="available">Available</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          )}

          {/* Form80G Cards - Mobile View */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {paginatedForms.map((form) => (
              <Box
                key={form.id}
                sx={{
                  border: "1px solid rgba(0, 0, 0, 0.10)",
                  borderRadius: "16px",
                  p: "14px 16px",
                  backgroundColor: "#fff",
                  boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
                }}
              >
                {/* Transaction ID */}
                <Box
                  sx={{
                    backgroundColor: "#F5F8ED",
                    borderRadius: "6px",
                    display: "inline-block",
                    padding: "10px",
                    width: "100%",
                    mb: "14px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#6A994E",
                    }}
                  >
                    Transaction ID -{" "}
                    <span style={{ color: "#1D1D1B", fontWeight: 700 }}>
                      {form.transactionId}
                    </span>
                  </Typography>
                </Box>

                {/* Date */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        color: "#686866",
                        mb: "10px",
                      }}
                    >
                      Donation Date
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#0B1215",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "block",
                        width: "100%",
                      }}
                    >
                      {form.date}
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ marginBlock: "16px" }} />
                {/* Amount & Download */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#686866",
                        fontWeight: 500,
                        mb: "10px",
                      }}
                    >
                      Amount
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#1D1D1B",
                      }}
                    >
                      {form.amount}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: "1px",
                      height: "40px",
                      backgroundColor: "#E0E0E0",
                      mx: 2,
                    }}
                  />
                  <Box>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon sx={{ fontSize: "16px" }} />}
                      onClick={() => handleDownload(form.id || form.transactionId, form)}
                      sx={{
                        backgroundColor: form.isDownloaded ? "#3BC37F" : "#6A994E",
                        borderRadius: "20px",
                        textTransform: "none",
                        fontSize: "12px",
                        fontWeight: 600,
                        padding: "8px 16px",
                        minWidth: "100px",
                        "&:hover": {
                          backgroundColor: form.isDownloaded ? "#2FA86F" : "#5A8B41",
                        },
                      }}
                    >
                      {form.isDownloaded ? "Downloaded" : "Download"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Mobile Pagination */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 3,
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* Back Button */}
            <Box
              onClick={() => setPage(Math.max(0, page - 1))}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                border: "1px solid #E0E0E0",
                backgroundColor: "#FFFFFF",
                cursor: page === 0 ? "not-allowed" : "pointer",
                opacity: page === 0 ? 0.5 : 1,
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 12L6 8L10 4"
                  stroke="#5E5E5E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>

            {/* Page Indicator */}
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#1D1D1B",
              }}
            >
              {filteredForms.length === 0 ? 0 : page + 1} / {Math.max(1, Math.ceil(filteredForms.length / 5))}
            </Typography>

            {/* Next Button */}
            <Box
              onClick={() =>
                setPage(Math.min(Math.ceil(filteredForms.length / 5) - 1, page + 1))
              }
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                border: "1px solid #E0E0E0",
                backgroundColor: "#FFFFFF",
                cursor:
                  page >= Math.ceil(filteredForms.length / 5) - 1
                    ? "not-allowed"
                    : "pointer",
                opacity: page >= Math.ceil(filteredForms.length / 5) - 1 ? 0.5 : 1,
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 4L10 8L6 12"
                  stroke="#5E5E5E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Form10B;
